import { UkDateInput, ukDate } from "@/components/UkDateInput";

const isFixed = (li) => Boolean(li.fixed) || (Number(li.hours || 0) === 0 && Number(li.amount || 0) > 0);
const lineAmt = (li) => (isFixed(li) ? Number(li.rate || 0) : Number(li.hours || 0) * Number(li.rate || 0));

import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, Plus, X, Trash2, FileText, PoundSterling, Download, Send, Tag, ChevronDown, CalendarRange } from "lucide-react";
import { statusTone, HeaderAccent } from "@/lib/status";
import { useAuth } from "@/context/AuthContext";
import { BANK } from "@/lib/company";

function monthRange() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const y = now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear();
  const m = (now.getMonth() + 1) % 12;
  const last = new Date(y, m + 1, 0).getDate();
  return { from: `${y}-${pad(m + 1)}-01`, to: `${y}-${pad(m + 1)}-${pad(last)}` };
}
const FREE_LINE = "First companion visit — 1 hour free";

export default function Billing() {
  const { user } = useAuth();
  const canEditPricing = (user?.permissions || []).includes("billing");
  const init = monthRange();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [services, setServices] = useState([]);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [savingPrice, setSavingPrice] = useState(false);
  const updatePrice = (idx, k, val) => setServices((arr) => arr.map((s, i) => (i === idx ? { ...s, [k]: val } : s)));
  const savePricing = async () => {
    setSavingPrice(true);
    try {
      await api.put("/services/pricing", services.map((s) => ({ name: s.name, price_from: Number(s.price_from || 0), unit: s.unit || "per hour", note: s.note || "" })));
      toast.success("Service prices updated — families will see the new prices");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingPrice(false); }
  };
  const openDetail = (i) => { setDetail(i); setItems((i.line_items || []).map((x) => ({ ...x }))); };
  const updateItem = (idx, k, val) => setItems((arr) => arr.map((li, i) => (i === idx ? { ...li, [k]: val } : li)));
  const editSubtotal = items.reduce((s, li) => s + lineAmt(li), 0);
  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/invoices/${detail.id}`, { line_items: items.map((li) => ({ date: li.date || "", description: li.description || "", hours: Number(li.hours || 0), rate: Number(li.rate || 0), fixed: isFixed(li) })) });
      toast.success("Invoice updated"); setDetail(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const sendInvoice = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${detail.id}/send`);
      toast.success(data.sent ? `Sent to ${data.sent} family login(s)` : "No family login is linked to this client yet");
      setDetail(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const downloadInvoice = (inv, its) => {
    const rows = its.map((li) => `<tr><td>${li.date || ""}</td><td>${li.description || ""}</td><td style="text-align:right">${li.hours}</td><td style="text-align:right">£${Number(li.rate).toFixed(2)}</td><td style="text-align:right">£${lineAmt(li).toFixed(2)}</td></tr>`).join("");
    const sub = its.reduce((s, li) => s + lineAmt(li), 0).toFixed(2);
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to download the invoice"); return; }
    w.document.write(`<html><head><title>${inv.invoice_number}</title><style>body{font-family:Arial,sans-serif;padding:40px;color:#1a2b28}h1{color:#0F5A54;margin:0}table{width:100%;border-collapse:collapse;margin-top:20px}th,td{border-bottom:1px solid #ddd;padding:8px;font-size:13px}th{text-align:left;background:#f4f4f0}.tot{margin-top:20px;text-align:right;font-size:18px;font-weight:bold;color:#0F5A54}</style></head><body><h1>Brightside Befriending Services</h1><h3>Invoice ${inv.invoice_number}</h3><p><b>Client:</b> ${inv.client_name}<br/><b>Period:</b> ${ukDate(inv.period.from)} → ${ukDate(inv.period.to)}</p><table><thead><tr><th>Date</th><th>Description</th><th>Hours</th><th>Rate</th><th>Amount</th></tr></thead><tbody>${rows}</tbody></table><p class="tot">Total: £${sub}</p><div style="margin-top:30px;padding:14px;border:1px solid #0F5A54;border-radius:6px;font-size:13px"><b>How to pay</b> — bank transfer, quoting ${inv.invoice_number} as the reference<br/>Account name: ${BANK.name}<br/>Account number: ${BANK.account} · Sort code: ${BANK.sort}<br/>Bank: ${BANK.bank}</div><script>window.onload=function(){window.print();}</script></body></html>`);
    w.document.close();
  };
  const [gen, setGen] = useState({ client_id: "", date_from: init.from, date_to: init.to, blank: false });
  const companionRate = Number(clients.find((c) => c.id === detail?.client_id)?.billing_rate || 23);
  const addService = (name) => {
    const s = services.find((x) => x.name === name);
    if (!s) return;
    const hourly = /hour/i.test(s.unit || "");
    setItems([...items, { date: detail.period.from, description: `${s.name}${hourly ? "" : ` (${s.unit})`}`, hours: hourly ? 1 : 0, rate: Number(s.price_from || 0), fixed: !hourly }]);
  };
  const addFree = () => {
    if (items.some((li) => /1 hour free/.test(li.description || ""))) { toast.warning("This invoice already has the free first visit"); return; }
    setItems([...items, { date: detail.period.from, description: FREE_LINE, hours: -1, rate: companionRate, fixed: false }]);
  };

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data));
  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)); api.get("/services").then((r) => setServices(r.data)); }, []);

  const generate = async (e) => {
    e.preventDefault();
    if (!gen.client_id) { toast.error("Select a client"); return; }
    try {
      const { blank, ...body } = gen;
      const { data } = await api.post(blank ? "/invoices/manual" : "/invoices/generate", body);
      if (blank) toast.success(`Blank invoice ${data.invoice_number} created — add your lines`);
      else if (data.line_items.length === 0) toast.warning("No planned visits in this period — empty invoice created");
      else toast.success(`Invoice ${data.invoice_number} created`);
      setOpen(false); load();
      if (blank || data.line_items.length === 0) openDetail(data);
    } catch (err) { toast.error(apiError(err)); }
  };
  const setStatusFor = async (id, status) => {
    if (status === "sent" && !window.confirm("Mark ready and email this invoice to the family now?")) return;
    try {
      const { data } = await api.put(`/invoices/${id}/status`, { status });
      if (status === "sent") {
        if (data.emailed_to?.length) toast.success(`Invoice emailed to ${data.emailed_to.join(", ")}`);
        if (data.email_failed?.length) toast.error(`Couldn't email ${data.email_failed.join(", ")} — check it's a real address on Family access`);
        if (data.no_family) toast.warning("This client has no family login, so nothing was emailed");
      } else toast.success("Invoice updated");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const monthlyRun = async () => {
    if (!window.confirm("Create next month's draft invoices for every client?")) return;
    try {
      const { data } = await api.post("/invoices/monthly-run");
      toast.success(`${data.created} draft invoice(s) created for ${ukDate(data.period.from)} → ${ukDate(data.period.to)}${data.skipped ? ` · ${data.skipped} already existed` : ""}`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const remove = async (i) => {
    if (!window.confirm(`Delete ${i.invoice_number}?`)) return;
    try { await api.delete(`/invoices/${i.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const input = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
  const outstanding = invoices.filter((i) => i.status !== "paid").reduce((s, i) => s + i.subtotal, 0);

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Billing & invoicing</h1>
          <p className="mt-1 text-ink-soft">Invoices are raised in advance. Drafts for next month are created automatically on the 25th; set one to “Ready” to email it to the family.</p>
          <HeaderAccent />
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={monthlyRun} data-testid="monthly-run-button"
            className="inline-flex items-center gap-2 rounded-lg border border-brand px-4 py-2.5 font-semibold text-brand transition-colors hover:bg-brand-light">
            <CalendarRange size={18} /> Generate next month
          </button>
          <button onClick={() => setOpen(true)} data-testid="generate-invoice-button"
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-dark">
            <Plus size={18} /> Create invoice
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
          <p className="text-sm text-ink-soft">Total invoices</p>
          <p className="font-heading text-3xl font-bold text-ink">{invoices.length}</p>
        </div>
        <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
          <p className="text-sm text-ink-soft">Outstanding</p>
          <p className="font-heading text-3xl font-bold text-gold-dark">£{outstanding.toFixed(2)}</p>
        </div>
        <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
          <p className="text-sm text-ink-soft">Paid</p>
          <p className="font-heading text-3xl font-bold text-sage-dark">£{invoices.filter((i) => i.status === "paid").reduce((s, i) => s + i.subtotal, 0).toFixed(2)}</p>
        </div>
      </div>

      {canEditPricing && (
        <div className="overflow-hidden rounded-xl border border-border bg-white shadow-sm" data-testid="service-pricing-card">
          <button onClick={() => setPricingOpen((v) => !v)} data-testid="toggle-pricing-editor"
            className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left hover:bg-ivory/60">
            <span className="flex items-center gap-2 font-heading text-lg font-semibold text-ink"><Tag size={18} className="text-brand" /> Edit service prices</span>
            <span className="flex items-center gap-2 text-sm text-ink-muted">Shown to families on the portal <ChevronDown size={18} className={`transition-transform ${pricingOpen ? "rotate-180" : ""}`} /></span>
          </button>
          {pricingOpen && (
            <div className="border-t border-border px-6 py-5">
              <div className="space-y-2">
                {services.map((s, idx) => (
                  <div key={s.name} data-testid={`price-row-${s.name}`} className="grid grid-cols-12 items-center gap-2 border-b border-border/60 py-2">
                    <span className="col-span-12 text-sm font-semibold text-ink sm:col-span-4">{s.name}</span>
                    <div className="col-span-4 flex items-center gap-1 sm:col-span-2">
                      <span className="text-ink-muted">£</span>
                      <input type="number" step="1" min="0" className={input} value={s.price_from ?? ""} onChange={(e) => updatePrice(idx, "price_from", e.target.value)} data-testid={`price-amount-${s.name}`} />
                    </div>
                    <input className={`${input} col-span-8 sm:col-span-3`} value={s.unit || ""} onChange={(e) => updatePrice(idx, "unit", e.target.value)} placeholder="per hour" data-testid={`price-unit-${s.name}`} />
                    <input className={`${input} col-span-12 sm:col-span-3`} value={s.note || ""} onChange={(e) => updatePrice(idx, "note", e.target.value)} placeholder="Note (optional)" data-testid={`price-note-${s.name}`} />
                  </div>
                ))}
              </div>
              <div className="mt-4 flex justify-end">
                <button onClick={savePricing} disabled={savingPrice} data-testid="save-pricing-button"
                  className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white hover:bg-brand-dark disabled:opacity-60">
                  {savingPrice ? "Saving…" : "Save prices"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-border bg-white shadow-sm">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead className="bg-ivory text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-6 py-3">Invoice</th><th className="px-6 py-3">Client</th>
                <th className="px-6 py-3">Period</th><th className="px-6 py-3">Amount</th>
                <th className="px-6 py-3">Status</th><th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {invoices.map((i) => (
                <tr key={i.id} data-testid={`invoice-row-${i.id}`} className="hover:bg-ivory/60">
                  <td className="px-6 py-4 font-semibold text-ink">{i.invoice_number}</td>
                  <td className="px-6 py-4 text-ink-soft">{i.client_name}</td>
                  <td className="px-6 py-4 text-xs text-ink-soft">{ukDate(i.period.from)} → {ukDate(i.period.to)}</td>
                  <td className="px-6 py-4 font-heading font-bold text-ink">£{i.subtotal.toFixed(2)}</td>
                  <td className="px-6 py-4">
                    <select value={i.status} onChange={(e) => setStatusFor(i.id, e.target.value)} data-testid={`invoice-status-${i.id}`}
                      className={`rounded-full px-3 py-1 text-xs font-semibold capitalize outline-none ${statusTone(i.status)}`}>
                      <option value="draft">Draft</option><option value="sent">Ready — emailed</option><option value="paid">Paid</option>
                    </select>
                    {i.emailed_to?.length > 0 && <p className="mt-1 text-[11px] text-sage-dark" data-testid={`invoice-emailed-${i.id}`}>Emailed {i.emailed_at?.slice(0, 10)}</p>}
                    {i.email_failed?.length > 0 && <p className="mt-1 text-[11px] text-coral-dark">Email failed</p>}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openDetail(i)} data-testid={`view-invoice-${i.id}`} className="rounded-lg border border-border px-2.5 py-1.5 text-ink hover:bg-secondary"><FileText size={14} /></button>
                      <button onClick={() => remove(i)} data-testid={`delete-invoice-${i.id}`} className="rounded-lg border border-border px-2.5 py-1.5 text-coral hover:bg-coral-light"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-ink-muted">
                  <Receipt size={28} className="mx-auto mb-2 opacity-40" /> No invoices yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <form onSubmit={generate} data-testid="invoice-form" className="relative z-10 max-h-[92vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-ink">Create invoice</h2>
              <button type="button" onClick={() => setOpen(false)}><X size={20} className="text-ink-muted" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2" data-testid="invoice-mode">
                {[[false, "From visits & agreement"], [true, "Blank — I'll add lines"]].map(([b, label]) => (
                  <button type="button" key={label} onClick={() => setGen({ ...gen, blank: b })} data-testid={`invoice-mode-${b ? "blank" : "auto"}`}
                    className={`rounded-lg border px-3 py-2 text-xs font-semibold ${gen.blank === b ? "border-brand bg-brand-light text-brand" : "border-border text-ink-soft"}`}>{label}</button>
                ))}
              </div>
              <p className="text-xs text-ink-muted">Invoices are billed in advance — the period defaults to next month. The first companion visit (1 hour) is free and is taken off automatically.</p>
              <div><label className="mb-1 block text-sm font-medium text-ink">Client *</label>
                <select data-testid="invoice-client-select" className={input} value={gen.client_id} onChange={(e) => setGen({ ...gen, client_id: e.target.value })}>
                  <option value="">Select client…</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name} (£{c.billing_rate}/hr)</option>)}
                </select></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="mb-1 block text-sm font-medium text-ink">From</label>
                  <UkDateInput value={gen.date_from} onChange={(v) => setGen({ ...gen, date_from: v })} testid="invoice-from-input" /></div>
                <div><label className="mb-1 block text-sm font-medium text-ink">To</label>
                  <UkDateInput value={gen.date_to} min={gen.date_from} onChange={(v) => setGen({ ...gen, date_to: v })} testid="invoice-to-input" /></div>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button type="button" onClick={() => setOpen(false)} className="flex-1 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary">Cancel</button>
              <button type="submit" data-testid="save-invoice-button" className="flex-1 rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark">{gen.blank ? "Create" : "Generate"}</button>
            </div>
          </form>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDetail(null)} />
          <div data-testid="invoice-detail" className="relative z-10 h-full w-full max-w-lg overflow-y-auto bg-white p-8 shadow-xl scrollbar-thin">
            <button onClick={() => setDetail(null)} className="absolute right-5 top-5 text-ink-muted hover:text-ink"><X size={22} /></button>
            <h2 className="font-heading text-2xl font-bold text-ink">{detail.invoice_number}</h2>
            <p className="mt-1 text-ink-soft">{detail.client_name}</p>
            <p className="text-xs text-ink-muted">{ukDate(detail.period.from)} → {ukDate(detail.period.to)}</p>

            <div className="mt-6 space-y-2">
              {items.map((li, idx) => (
                <div key={idx} className="grid grid-cols-12 items-center gap-2 border-b border-border py-2">
                  <input className={`${input} col-span-5`} value={li.description} onChange={(e) => updateItem(idx, "description", e.target.value)} placeholder="Description" data-testid={`li-desc-${idx}`} />
                  <input type="number" step="0.25" className={`${input} col-span-2`} value={li.hours} onChange={(e) => updateItem(idx, "hours", e.target.value)} data-testid={`li-hours-${idx}`} />
                  <input type="number" step="0.5" className={`${input} col-span-2`} value={li.rate} onChange={(e) => updateItem(idx, "rate", e.target.value)} data-testid={`li-rate-${idx}`} />
                  <span className="col-span-2 text-right text-sm font-semibold text-ink">{lineAmt(li) < 0 ? "−" : ""}£{Math.abs(lineAmt(li)).toFixed(2)}</span>
                  <button onClick={() => setItems(items.filter((_, i) => i !== idx))} className="col-span-1 flex justify-end text-coral" data-testid={`li-remove-${idx}`}><Trash2 size={14} /></button>
                </div>
              ))}
              <div className="flex flex-wrap gap-2 pt-1">
                <button onClick={() => setItems([...items, { date: detail.period.from, description: "", hours: 1, rate: items[0]?.rate || companionRate }])} data-testid="add-line-item" className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-1.5 text-sm text-ink-soft hover:bg-secondary"><Plus size={14} /> Add line</button>
                <select value="" onChange={(e) => addService(e.target.value)} data-testid="add-service-line" className="rounded-lg border border-dashed border-border px-2 py-1.5 text-sm text-ink-soft">
                  <option value="">+ Add a service…</option>
                  {services.map((s) => <option key={s.name} value={s.name}>{s.name} — £{s.price_from} {s.unit}</option>)}
                </select>
                <button onClick={addFree} data-testid="add-free-visit" className="inline-flex items-center gap-1.5 rounded-lg border border-sage px-3 py-1.5 text-sm font-semibold text-sage-dark hover:bg-sage-light">First visit free (−£{companionRate.toFixed(2)})</button>
              </div>
            </div>

            {detail.total_miles > 0 && <p className="mt-3 text-xs text-ink-muted">Mileage logged this period: {detail.total_miles} miles</p>}
            <div className="mt-4 flex items-center justify-between rounded-lg bg-brand-light/60 p-4">
              <span className="flex items-center gap-1.5 font-semibold text-brand"><PoundSterling size={16} /> Total</span>
              <span className="font-heading text-2xl font-bold text-ink">£{editSubtotal.toFixed(2)}</span>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <button onClick={saveEdit} disabled={busy} data-testid="save-invoice-edit" className="rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark disabled:opacity-60">Save changes</button>
              <button onClick={() => downloadInvoice(detail, items)} data-testid="download-invoice" className="inline-flex items-center justify-center gap-2 rounded-lg border border-border py-2.5 font-medium text-ink hover:bg-secondary"><Download size={16} /> Download</button>
              <button onClick={sendInvoice} disabled={busy} data-testid="send-invoice" className="col-span-2 inline-flex items-center justify-center gap-2 rounded-lg border border-brand bg-brand/5 py-2.5 font-semibold text-brand hover:bg-brand/10"><Send size={16} /> Send to family inbox</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
